import * as React from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'
import { ConfigUiHint, ConfigUiHints, JsonSchema } from '../../../types'
import { SchemaForm } from '../SchemaForm'

interface ObjectFieldProps {
  label: string
  schema: JsonSchema
  value: Record<string, unknown>
  hints: ConfigUiHints
  path: Array<string | number>
  hint?: ConfigUiHint
  onChange: (path: Array<string | number>, value: unknown) => void
  disabled?: boolean
}

const Wrapper = styled.details`
  margin-bottom: 12px;
  border-radius: ${(props) => props.theme.radius};
  overflow: hidden;

  &[open] > summary .obj-chevron {
    transform: rotate(90deg);
  }
`

const Summary = styled.summary`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: ${(props) => props.theme.secondaryPanel};
  cursor: pointer;
  list-style: none;
  user-select: none;
  border-radius: ${(props) => props.theme.radius};
  transition: background-color 0.15s ease;
  min-height: 44px;
  flex-wrap: wrap;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.boxHover};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${(props) => props.theme.accent};
    outline: none;
  }

  @media (max-width: ${screenSizes.XS}px) {
    padding: 10px;
  }
`

const Chevron = styled.span`
  display: inline-flex;
  font-size: 12px;
  color: ${(props) => props.theme.listDesc};
  transition: transform 0.2s ease;
  min-width: 14px;
`

const Label = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.white};
  font-family: 'NunitoSansSemiBold';
  flex: 1;
`

const PropCount = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.listDesc};
`

const HelpText = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.gray};
  padding: 4px 12px 0;
`

const Content = styled.div`
  padding: 12px;
  background-color: ${(props) => props.theme.primary};
  border-top: 1px solid ${(props) => props.theme.secondaryPanel};

  @media (max-width: ${screenSizes.XS}px) {
    padding: 8px;
  }
`

export function ObjectField({
  label,
  schema,
  value,
  hints,
  path,
  hint,
  onChange,
  disabled,
}: ObjectFieldProps) {
  const propCount = Object.keys(schema.properties ?? {}).length
  const help = hint?.help ?? schema.description

  return (
    <Wrapper>
      <Summary>
        <Chevron className="obj-chevron">&#9654;</Chevron>
        <Label>{label}</Label>
        {propCount > 0 && (
          <PropCount>
            {propCount} field{propCount !== 1 ? 's' : ''}
          </PropCount>
        )}
      </Summary>
      {help && <HelpText>{help}</HelpText>}
      <Content>
        <SchemaForm
          schema={schema}
          value={value}
          hints={hints}
          path={path}
          onChange={onChange}
          disabled={disabled}
        />
      </Content>
    </Wrapper>
  )
}
